import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";

export type AuthUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
};

export type AuthState = {
  session: Session | null;
  user: AuthUser | null;
  isDemo: boolean;
  isPasswordRecovery: boolean;
};

export type AuthStateListener = (state: AuthState) => void;

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const demoSessionKey = "cppp.demoSession";

export const hasSupabaseConfig = Boolean(supabaseUrl && supabaseAnonKey);

export const supabase: SupabaseClient | null = hasSupabaseConfig
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        // App owns explicit code exchange; retain SDK handling of implicit links.
        detectSessionInUrl: typeof window === "undefined" ||
          !new URLSearchParams(window.location?.search ?? "").has("code")
      }
    })
  : null;

export const demoUser: AuthUser = {
  id: "demo-prescriber",
  email: "demo.prescriber@companionpharmacy.com",
  firstName: "Jill",
  lastName: "Austin"
};

export function toAuthUser(session: Session | null): AuthUser | null {
  if (!session?.user) return null;

  const metadata = session.user.user_metadata ?? {};
  return {
    id: session.user.id,
    email: session.user.email ?? "",
    firstName: typeof metadata.first_name === "string" ? metadata.first_name : "",
    lastName: typeof metadata.last_name === "string" ? metadata.last_name : ""
  };
}

export async function getCurrentAuthState(): Promise<AuthState> {
  if (!supabase) {
    const hasDemoSession = window.localStorage.getItem(demoSessionKey) === "true";
    return { session: null, user: hasDemoSession ? demoUser : null, isDemo: hasDemoSession, isPasswordRecovery: false };
  }

  const { data } = await supabase.auth.getSession();
  return { session: data.session, user: toAuthUser(data.session), isDemo: false, isPasswordRecovery: false };
}

export function onAuthStateChange(listener: AuthStateListener): () => void {
  if (!supabase) {
    return () => undefined;
  }

  const {
    data: { subscription }
  } = supabase.auth.onAuthStateChange((event, session) => {
    listener({ session, user: toAuthUser(session), isDemo: false, isPasswordRecovery: event === "PASSWORD_RECOVERY" });
  });

  return () => subscription.unsubscribe();
}

export async function getAccessToken(): Promise<string | null> {
  if (!supabase) return null;

  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  if (!supabase) return;

  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

function authRedirectUrl(path = "/"): string {
  return `${window.location.origin}${path}`;
}

export async function sendSignInLink(email: string): Promise<void> {
  if (!supabase) return;

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: authRedirectUrl("/auth/otp/"),
      shouldCreateUser: false
    }
  });
  if (error) throw error;
}

const pendingLinkExchanges = new Map<string, Promise<Session | null>>();

function exchangeOnce(key: string, exchange: () => Promise<Session | null>): Promise<Session | null> {
  const pending = pendingLinkExchanges.get(key);
  if (pending) return pending;
  const result = exchange().finally(() => pendingLinkExchanges.delete(key));
  pendingLinkExchanges.set(key, result);
  return result;
}

export async function verifyEmailTokenHash(tokenHash: string, type: string): Promise<Session | null> {
  if (!supabase) return null;

  const allowedTypes = new Set(["signup", "invite", "magiclink", "recovery", "email_change", "email"]);
  if (!allowedTypes.has(type)) throw new Error("Unsupported authentication link type");
  return exchangeOnce(`token:${type}:${tokenHash}`, async () => {
    const { data, error } = await supabase!.auth.verifyOtp({
      token_hash: tokenHash,
      type: type as "email"
    });
    if (error) throw error;
    if (!data.session) throw new Error("Authentication link returned no session");
    return data.session;
  });
}

export async function exchangeAuthCode(code: string): Promise<Session | null> {
  if (!supabase) return null;

  return exchangeOnce(`code:${code}`, async () => {
    const { data, error } = await supabase!.auth.exchangeCodeForSession(code);
    if (error) throw error;
    if (!data.session) throw new Error("Authentication link returned no session");
    return data.session;
  });
}

export async function updatePassword(password: string): Promise<void> {
  if (!supabase) return;

  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
}

export async function sendPasswordReset(email: string): Promise<void> {
  if (!supabase) return;

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: authRedirectUrl("/reset_password/")
  });
  if (error) throw error;
}

export async function signInDemo(): Promise<void> {
  window.localStorage.setItem(demoSessionKey, "true");
}

export async function signOut(): Promise<void> {
  if (!supabase) {
    window.localStorage.removeItem(demoSessionKey);
    return;
  }

  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}
